/**
 * CodeMirror 6 theme for the formula editor, built entirely from the project's design tokens
 * (src/styles/tokens.css) — no third-party CM theme package. One extension parameterized by
 * `singleLine`: only the shape (pill vs. box) differs between the inline bar and the expanded
 * panel, never the palette.
 */

import { HighlightStyle, syntaxHighlighting } from '@codemirror/language'
import { EditorView } from '@codemirror/view'
import { tags } from '@lezer/highlight'
import type { Extension } from '@codemirror/state'

const highlightStyle = HighlightStyle.define([
  { tag: tags.number, color: 'var(--text)' },
  { tag: tags.string, color: 'var(--text)' },
  { tag: tags.keyword, color: 'var(--brand)' },
  { tag: tags.function(tags.variableName), color: 'var(--brand)' },
  { tag: tags.variableName, color: 'var(--text)' },
  { tag: tags.operator, color: 'var(--muted)' },
  { tag: tags.paren, color: 'var(--muted)' },
  { tag: tags.punctuation, color: 'var(--muted)' },
  { tag: tags.invalid, color: 'var(--danger)', textDecoration: 'underline wavy' },
])

export function formulaTheme(
  singleLine: boolean,
  variant: 'bar' | 'panel' | 'cell' = singleLine ? 'bar' : 'panel',
): Extension {
  const isCell = variant === 'cell'
  return [
    EditorView.theme({
      '&': {
        width: '100%',
        backgroundColor: isCell ? 'transparent' : 'var(--surface)',
        color: 'var(--text)',
        fontFamily: isCell ? 'inherit' : 'var(--font-mono)',
        fontSize: isCell ? 'inherit' : '0.75rem',
        border: isCell ? 'none' : '1px solid var(--border)',
        borderRadius: isCell ? '0' : (singleLine ? 'var(--radius-full)' : 'var(--radius-lg)'),
        ...(isCell ? { minHeight: '1.5rem', maxHeight: 'none', height: '100%' } : singleLine ? { minHeight: '1.75rem', maxHeight: '1.75rem' } : { height: '100%' }),
        transition: 'border-color 0.15s ease, border-radius 0.15s ease',
      },
      '&.cm-focused': {
        outline: 'none',
        borderRadius: isCell ? '0' : (singleLine ? 'var(--radius-lg)' : 'var(--radius-lg)'),
        ...(singleLine && !isCell ? { maxHeight: 'none' } : {}),
      },
      '&.cm-focused, &.formula-editor--focused': { borderColor: isCell ? 'transparent' : 'var(--focus)' },
      '&.formula-editor--error': { borderColor: isCell ? 'transparent' : 'var(--danger)' },
      '.cm-content': {
        padding: isCell ? '0 0.25rem' : singleLine ? '0.25rem 2rem 0.25rem 0.625rem' : '0.375rem 0.5rem 2.75rem',
        caretColor: 'var(--text)',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        fontFamily: 'inherit',
        fontSize: 'inherit',
      },
      '.cm-scroller': {
        fontFamily: 'inherit',
        lineHeight: '1.4',
        overflow: isCell ? 'visible' : singleLine ? 'hidden' : 'auto',
      },
      '&.cm-focused .cm-scroller': {
        overflow: isCell ? 'visible' : 'auto',
      },
      '.cm-placeholder': { color: 'var(--muted)' },
      '.cm-tooltip': {
        backgroundColor: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        boxShadow: 'var(--shadow)',
        zIndex: 100,
      },
      '.cm-tooltip-autocomplete': {
        zIndex: 100,
      },
      '.cm-tooltip-autocomplete ul li[aria-selected]': {
        backgroundColor: 'color-mix(in srgb, var(--brand) 10%, transparent)',
        color: 'var(--brand)',
      },
      // One glyph per kind of thing the dropdown offers, so a document, a sheet, a column and a
      // function are told apart at a glance rather than by reading the label. CM6 renders the
      // icon from `content`, keyed by the entry's `type`.
      '.cm-completionIcon': { width: '1.1em', textAlign: 'center', opacity: '0.8', paddingRight: '0.25em' },
      '.cm-completionIcon-function::after': { content: '"ƒ"' },
      '.cm-completionIcon-document::after': { content: '"▤"' },
      '.cm-completionIcon-sheet::after': { content: '"▦"' },
      '.cm-completionIcon-column::after': { content: '"▮"' },
      '.cm-tooltip-autocomplete .cm-completionDetail': {
        marginLeft: '0.5em',
        color: 'var(--muted)',
        fontStyle: 'normal',
        fontSize: '0.9em',
      },
      '.cm-formula-signature': {
        padding: '0.375rem 0.5rem',
        fontFamily: 'var(--font-mono)',
        fontSize: '0.75rem',
        color: 'var(--muted)',
        maxWidth: '24rem',
        position: 'relative',
        paddingRight: '1.75rem',
      },
      '.cm-panels .cm-formula-signature': { maxWidth: 'none' },
      // The sidebar's ✕ / ✓ / delete buttons float over the editor's bottom-right corner; keep the docked help above them.
      '.cm-panels-bottom': { borderTop: '1px solid var(--border)', backgroundColor: 'transparent', marginBottom: '2.5rem' },
      '.cm-formula-signature-close, .cm-formula-signature-show': {
        border: 'none',
        cursor: 'pointer',
        lineHeight: '1',
        fontFamily: 'var(--font-sans)',
      },
      '.cm-formula-signature-close': {
        position: 'absolute',
        top: '0.25rem',
        right: '0.25rem',
        width: '1.25rem',
        height: '1.25rem',
        borderRadius: '999px',
        background: 'transparent',
        color: 'var(--muted)',
        fontSize: '1rem',
      },
      '.cm-formula-signature-close:hover': { color: 'var(--text)' },
      '.cm-formula-signature-show': {
        display: 'block',
        margin: '0.125rem',
        width: '1.25rem',
        height: '1.25rem',
        borderRadius: '0.25rem',
        background: 'var(--brand)',
        color: 'white',
        fontWeight: '700',
        fontSize: '0.8rem',
      },
      '.cm-formula-signature-name': { color: 'var(--brand)' },
      '.cm-formula-signature b': { color: 'var(--brand)', fontWeight: 'normal' },
      '.cm-formula-signature-desc': {
        marginTop: '0.25rem',
        color: 'var(--muted)',
        fontFamily: 'var(--font-sans)',
        whiteSpace: 'normal',
      },
    }),
    syntaxHighlighting(highlightStyle),
  ]
}
